export type BenefitStacking = 'stackable' | 'exclusive';
export type BenefitScope =
  | { readonly kind: 'order' }
  | { readonly kind: 'products'; readonly productIds: readonly string[] };

export type BenefitDefinitionTerms =
  | {
      readonly kind: 'fixed';
      readonly amountMinor: number;
      readonly scope: BenefitScope;
      readonly minimumSpendMinor: number;
      readonly stacking: BenefitStacking;
    }
  | {
      readonly kind: 'percentage';
      readonly basisPoints: number;
      readonly scope: BenefitScope;
      readonly minimumSpendMinor: number;
      readonly stacking: BenefitStacking;
    };

export interface BenefitMutationContext {
  readonly actorId: string;
  readonly idempotencyKey: string;
  readonly at: string;
}

export type PublishBenefitDefinitionCommand = BenefitDefinitionTerms & {
  readonly definitionId: string;
  readonly expectedRevision: number;
  readonly enabled: boolean;
};

export type BenefitDefinitionSnapshot = BenefitDefinitionTerms & {
  readonly definitionId: string;
  readonly revision: number;
  readonly enabled: boolean;
  readonly publishedAt: string;
  readonly publishedBy: string;
};

export interface PublishMilestoneRuleCommand {
  readonly ruleId: string;
  readonly expectedRevision: number;
  readonly enabled: boolean;
  readonly seedThreshold: number;
  readonly definitionId: string;
  readonly definitionRevision: number;
  readonly validDays: number;
}

export interface MilestoneRuleSnapshot extends PublishMilestoneRuleCommand {
  readonly revision: number;
  readonly publishedAt: string;
  readonly publishedBy: string;
}

export type CouponStatus = 'available' | 'reserved' | 'redeemed' | 'expired' | 'voided';

export interface CouponReservation {
  readonly checkoutRef: string;
  readonly candidateSignature: string;
  readonly reservedAt: string;
  readonly expiresAt: string;
}

export interface IssuedCouponSnapshot {
  readonly couponId: string;
  readonly customerId: string;
  readonly definitionId: string;
  readonly definitionRevision: number;
  readonly milestoneRuleId: string;
  readonly milestoneRuleRevision: number;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly status: CouponStatus;
  readonly version: number;
  readonly reservation?: CouponReservation;
  readonly redeemedOrderId?: string;
  readonly redeemedAt?: string;
  readonly voidReason?: string;
}

export type ValidatedPricingBenefit =
  | {
      readonly referenceId: string;
      readonly kind: 'fixed';
      readonly amountMinor: number;
      readonly scope: BenefitScope;
      readonly stacking: BenefitStacking;
    }
  | {
      readonly referenceId: string;
      readonly kind: 'percentage';
      readonly basisPoints: number;
      readonly scope: BenefitScope;
      readonly stacking: BenefitStacking;
    };

export interface IssueEligibleMilestonesCommand {
  readonly customerId: string;
  readonly seedBalance: number;
  readonly idempotencyKey: string;
  readonly at: string;
}

export interface ReserveCouponCommand {
  readonly couponId: string;
  readonly customerId: string;
  readonly expectedVersion: number;
  readonly checkoutRef: string;
  readonly candidateSignature: string;
  readonly orderTotalMinor: number;
  readonly productIds: readonly string[];
  readonly reservationExpiresAt: string;
}

export interface ReserveCouponResult {
  readonly coupon: IssuedCouponSnapshot;
  readonly validatedBenefit: ValidatedPricingBenefit;
}

export interface ReleaseCouponReservationCommand {
  readonly couponId: string;
  readonly expectedVersion: number;
  readonly checkoutRef: string;
}

export interface CommitCouponRedemptionCommand {
  readonly couponId: string;
  readonly expectedVersion: number;
  readonly checkoutRef: string;
  readonly orderId: string;
}

export interface VoidCouponCommand {
  readonly couponId: string;
  readonly expectedVersion: number;
  readonly reason: string;
}

interface StoredCoupon {
  readonly couponId: string;
  readonly customerId: string;
  readonly definitionId: string;
  readonly definitionRevision: number;
  readonly milestoneRuleId: string;
  readonly milestoneRuleRevision: number;
  readonly issuedAt: string;
  readonly expiresAt: string;
  status: CouponStatus;
  version: number;
  reservation?: CouponReservation;
  redeemedOrderId?: string;
  redeemedAt?: string;
  voidReason?: string;
}

interface IdempotentResult {
  readonly signature: string;
  readonly result: unknown;
}

const clone = <T>(value: T): T => structuredClone(value);
const DAY_MS = 86_400_000;

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, stableValue(entry)]),
  );
  return value;
};

const signatureOf = (kind: string, context: unknown, command: unknown): string =>
  JSON.stringify(stableValue({ kind, context, command }));

const assertText = (value: string, code: string): string => {
  const accepted = value?.trim();
  if (!accepted) throw new Error(code);
  return accepted;
};

const assertTime = (value: string, code = 'BENEFIT_TIMESTAMP_INVALID'): number => {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) throw new Error(code);
  return parsed;
};

const assertRevision = (value: number): void => {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error('BENEFIT_EXPECTED_REVISION_INVALID');
};

const assertVersion = (value: number): void => {
  if (!Number.isSafeInteger(value) || value < 1) throw new Error('BENEFIT_EXPECTED_VERSION_INVALID');
};

const normalizeScope = (scope: BenefitScope): BenefitScope => {
  if (scope.kind === 'order') return { kind: 'order' };
  if (scope.kind !== 'products' || scope.productIds.length === 0) throw new Error('BENEFIT_SCOPE_INVALID');
  const productIds = [...new Set(scope.productIds.map((id) => assertText(id, 'BENEFIT_PRODUCT_ID_REQUIRED')))].sort();
  if (productIds.length === 0) throw new Error('BENEFIT_SCOPE_INVALID');
  return { kind: 'products', productIds };
};

const validateTerms = (command: PublishBenefitDefinitionCommand): BenefitDefinitionTerms => {
  const scope = normalizeScope(command.scope);
  if (!Number.isSafeInteger(command.minimumSpendMinor) || command.minimumSpendMinor < 0) {
    throw new Error('BENEFIT_MINIMUM_SPEND_INVALID');
  }
  if (command.stacking !== 'stackable' && command.stacking !== 'exclusive') throw new Error('BENEFIT_STACKING_INVALID');
  if (command.kind === 'fixed') {
    if (!Number.isSafeInteger(command.amountMinor) || command.amountMinor <= 0) throw new Error('BENEFIT_FIXED_AMOUNT_INVALID');
    return { kind: 'fixed', amountMinor: command.amountMinor, scope, minimumSpendMinor: command.minimumSpendMinor, stacking: command.stacking };
  }
  if (command.kind === 'percentage') {
    if (!Number.isSafeInteger(command.basisPoints) || command.basisPoints <= 0 || command.basisPoints > 10_000) {
      throw new Error('BENEFIT_PERCENTAGE_INVALID');
    }
    return { kind: 'percentage', basisPoints: command.basisPoints, scope, minimumSpendMinor: command.minimumSpendMinor, stacking: command.stacking };
  }
  throw new Error('BENEFIT_KIND_INVALID');
};

export function createBenefitAuthority() {
  const definitionHistory = new Map<string, BenefitDefinitionSnapshot[]>();
  const milestoneHistory = new Map<string, MilestoneRuleSnapshot[]>();
  const coupons = new Map<string, StoredCoupon>();
  const issuedMilestoneKeys = new Set<string>();
  const idempotency = new Map<string, IdempotentResult>();
  let couponSequence = 0;

  const assertContext = (context: BenefitMutationContext): void => {
    assertText(context.actorId, 'BENEFIT_ACTOR_REQUIRED');
    assertText(context.idempotencyKey, 'BENEFIT_IDEMPOTENCY_REQUIRED');
    assertTime(context.at);
  };

  const run = <T>(kind: string, context: BenefitMutationContext | null, idempotencyKey: string, command: unknown, action: () => T): T => {
    if (context) assertContext(context);
    const key = assertText(idempotencyKey, 'BENEFIT_IDEMPOTENCY_REQUIRED');
    const signature = signatureOf(kind, context, command);
    const replay = idempotency.get(key);
    if (replay) {
      if (replay.signature !== signature) throw new Error('BENEFIT_IDEMPOTENCY_CONFLICT');
      return clone(replay.result as T);
    }
    const result = action();
    idempotency.set(key, { signature, result: clone(result) });
    return clone(result);
  };

  const latestDefinition = (definitionId: string): BenefitDefinitionSnapshot | null => {
    const history = definitionHistory.get(definitionId);
    return history?.length ? history[history.length - 1] : null;
  };

  const definitionAt = (definitionId: string, revision: number): BenefitDefinitionSnapshot => {
    const found = definitionHistory.get(definitionId)?.find((entry) => entry.revision === revision);
    if (!found) throw new Error('BENEFIT_DEFINITION_REVISION_NOT_FOUND');
    return found;
  };

  const latestMilestone = (ruleId: string): MilestoneRuleSnapshot | null => {
    const history = milestoneHistory.get(ruleId);
    return history?.length ? history[history.length - 1] : null;
  };

  const couponSnapshot = (coupon: StoredCoupon): IssuedCouponSnapshot => clone({
    couponId: coupon.couponId, customerId: coupon.customerId,
    definitionId: coupon.definitionId, definitionRevision: coupon.definitionRevision,
    milestoneRuleId: coupon.milestoneRuleId, milestoneRuleRevision: coupon.milestoneRuleRevision,
    issuedAt: coupon.issuedAt, expiresAt: coupon.expiresAt, status: coupon.status, version: coupon.version,
    ...(coupon.reservation ? { reservation: clone(coupon.reservation) } : {}),
    ...(coupon.redeemedOrderId ? { redeemedOrderId: coupon.redeemedOrderId } : {}),
    ...(coupon.redeemedAt ? { redeemedAt: coupon.redeemedAt } : {}),
    ...(coupon.voidReason ? { voidReason: coupon.voidReason } : {}),
  });

  const requireCoupon = (couponId: string): StoredCoupon => {
    const accepted = assertText(couponId, 'BENEFIT_COUPON_ID_REQUIRED');
    const coupon = coupons.get(accepted);
    if (!coupon) throw new Error('BENEFIT_COUPON_NOT_FOUND');
    return coupon;
  };

  const markCouponExpiredIfDue = (coupon: StoredCoupon, at: string): void => {
    if (coupon.status === 'redeemed' || coupon.status === 'voided' || coupon.status === 'expired') return;
    if (assertTime(at) >= assertTime(coupon.expiresAt)) {
      coupon.status = 'expired';
      coupon.version += 1;
      delete coupon.reservation;
    }
  };

  const releaseReservationIfDue = (coupon: StoredCoupon, at: string): void => {
    if (coupon.status !== 'reserved' || !coupon.reservation) return;
    if (assertTime(at) >= assertTime(coupon.reservation.expiresAt)) {
      coupon.status = 'available';
      coupon.version += 1;
      delete coupon.reservation;
    }
  };

  const validatedBenefitFor = (coupon: StoredCoupon): ValidatedPricingBenefit => {
    const terms = definitionAt(coupon.definitionId, coupon.definitionRevision);
    const base = { referenceId: coupon.couponId, scope: clone(terms.scope), stacking: terms.stacking } as const;
    return terms.kind === 'fixed'
      ? { ...base, kind: 'fixed', amountMinor: terms.amountMinor }
      : { ...base, kind: 'percentage', basisPoints: terms.basisPoints };
  };

  return {
    publishDefinition(context: BenefitMutationContext, command: PublishBenefitDefinitionCommand): BenefitDefinitionSnapshot {
      return run('publishDefinition', context, context.idempotencyKey, command, () => {
        const definitionId = assertText(command.definitionId, 'BENEFIT_DEFINITION_ID_REQUIRED');
        assertRevision(command.expectedRevision);
        const current = latestDefinition(definitionId);
        const currentRevision = current?.revision ?? 0;
        if (command.expectedRevision !== currentRevision) throw new Error('BENEFIT_DEFINITION_REVISION_CONFLICT');
        const terms = validateTerms(command);
        const snapshot: BenefitDefinitionSnapshot = {
          definitionId, revision: currentRevision + 1, enabled: command.enabled,
          ...terms, publishedAt: context.at, publishedBy: context.actorId.trim(),
        };
        const history = definitionHistory.get(definitionId) ?? [];
        definitionHistory.set(definitionId, [...history, snapshot]);
        return snapshot;
      });
    },

    definition(definitionId: string, revision?: number): BenefitDefinitionSnapshot | null {
      const accepted = assertText(definitionId, 'BENEFIT_DEFINITION_ID_REQUIRED');
      if (revision === undefined) return clone(latestDefinition(accepted));
      const found = definitionHistory.get(accepted)?.find((entry) => entry.revision === revision);
      return found ? clone(found) : null;
    },

    publishMilestoneRule(context: BenefitMutationContext, command: PublishMilestoneRuleCommand): MilestoneRuleSnapshot {
      return run('publishMilestoneRule', context, context.idempotencyKey, command, () => {
        const ruleId = assertText(command.ruleId, 'BENEFIT_MILESTONE_RULE_ID_REQUIRED');
        const definitionId = assertText(command.definitionId, 'BENEFIT_DEFINITION_ID_REQUIRED');
        assertRevision(command.expectedRevision);
        if (!Number.isSafeInteger(command.seedThreshold) || command.seedThreshold < 0) throw new Error('BENEFIT_SEED_THRESHOLD_INVALID');
        if (!Number.isSafeInteger(command.definitionRevision) || command.definitionRevision < 1) throw new Error('BENEFIT_DEFINITION_REVISION_INVALID');
        if (!Number.isSafeInteger(command.validDays) || command.validDays < 1) throw new Error('BENEFIT_VALID_DAYS_INVALID');
        definitionAt(definitionId, command.definitionRevision);
        const current = latestMilestone(ruleId);
        const currentRevision = current?.revision ?? 0;
        if (command.expectedRevision !== currentRevision) throw new Error('BENEFIT_MILESTONE_REVISION_CONFLICT');
        const snapshot: MilestoneRuleSnapshot = {
          ...command, ruleId, definitionId, revision: currentRevision + 1,
          publishedAt: context.at, publishedBy: context.actorId.trim(),
        };
        const history = milestoneHistory.get(ruleId) ?? [];
        milestoneHistory.set(ruleId, [...history, snapshot]);
        return snapshot;
      });
    },

    issueEligibleMilestones(command: IssueEligibleMilestonesCommand): readonly IssuedCouponSnapshot[] {
      return run('issueEligibleMilestones', null, command.idempotencyKey, command, () => {
        const customerId = assertText(command.customerId, 'BENEFIT_CUSTOMER_ID_REQUIRED');
        assertTime(command.at);
        if (!Number.isSafeInteger(command.seedBalance) || command.seedBalance < 0) throw new Error('BENEFIT_SEED_BALANCE_INVALID');
        const now = assertTime(command.at);
        const issued: IssuedCouponSnapshot[] = [];
        const currentRules = [...milestoneHistory.values()]
          .map((history) => history[history.length - 1])
          .filter((rule): rule is MilestoneRuleSnapshot => Boolean(rule?.enabled))
          .sort((left, right) => left.seedThreshold - right.seedThreshold || left.ruleId.localeCompare(right.ruleId));
        for (const rule of currentRules) {
          if (command.seedBalance < rule.seedThreshold) continue;
          const issueKey = `${customerId}::${rule.ruleId}::${rule.revision}`;
          if (issuedMilestoneKeys.has(issueKey)) continue;
          const currentDefinition = latestDefinition(rule.definitionId);
          if (!currentDefinition?.enabled) continue;
          definitionAt(rule.definitionId, rule.definitionRevision);
          const couponId = `coupon:${++couponSequence}`;
          const coupon: StoredCoupon = {
            couponId, customerId, definitionId: rule.definitionId, definitionRevision: rule.definitionRevision,
            milestoneRuleId: rule.ruleId, milestoneRuleRevision: rule.revision,
            issuedAt: command.at, expiresAt: new Date(now + rule.validDays * DAY_MS).toISOString(),
            status: 'available', version: 1,
          };
          coupons.set(couponId, coupon);
          issuedMilestoneKeys.add(issueKey);
          issued.push(couponSnapshot(coupon));
        }
        return issued;
      });
    },

    reserve(context: BenefitMutationContext, command: ReserveCouponCommand): ReserveCouponResult {
      return run('reserve', context, context.idempotencyKey, command, () => {
        const coupon = requireCoupon(command.couponId);
        markCouponExpiredIfDue(coupon, context.at);
        if (coupon.status === 'expired') throw new Error('BENEFIT_COUPON_EXPIRED');
        releaseReservationIfDue(coupon, context.at);
        assertVersion(command.expectedVersion);
        if (coupon.version !== command.expectedVersion) throw new Error('BENEFIT_COUPON_VERSION_CONFLICT');
        if (coupon.status !== 'available') throw new Error('BENEFIT_COUPON_NOT_AVAILABLE');
        const customerId = assertText(command.customerId, 'BENEFIT_CUSTOMER_ID_REQUIRED');
        if (coupon.customerId !== customerId) throw new Error('BENEFIT_COUPON_CUSTOMER_MISMATCH');
        const currentDefinition = latestDefinition(coupon.definitionId);
        if (!currentDefinition?.enabled) throw new Error('BENEFIT_DEFINITION_DISABLED');
        const terms = definitionAt(coupon.definitionId, coupon.definitionRevision);
        if (!Number.isSafeInteger(command.orderTotalMinor) || command.orderTotalMinor < 0) throw new Error('BENEFIT_ORDER_TOTAL_INVALID');
        if (command.orderTotalMinor < terms.minimumSpendMinor) throw new Error('BENEFIT_MINIMUM_SPEND_NOT_MET');
        if (terms.scope.kind === 'products') {
          const candidateProducts = new Set(command.productIds.map((id) => assertText(id, 'BENEFIT_PRODUCT_ID_REQUIRED')));
          if (!terms.scope.productIds.some((productId) => candidateProducts.has(productId))) throw new Error('BENEFIT_SCOPE_NOT_ELIGIBLE');
        }
        const checkoutRef = assertText(command.checkoutRef, 'BENEFIT_CHECKOUT_REF_REQUIRED');
        const candidateSignature = assertText(command.candidateSignature, 'BENEFIT_CANDIDATE_SIGNATURE_REQUIRED');
        const reservationAt = assertTime(context.at);
        const reservationExpiresAt = assertTime(command.reservationExpiresAt, 'BENEFIT_RESERVATION_EXPIRY_INVALID');
        if (reservationExpiresAt <= reservationAt || reservationExpiresAt > assertTime(coupon.expiresAt)) {
          throw new Error('BENEFIT_RESERVATION_EXPIRY_INVALID');
        }
        coupon.status = 'reserved';
        coupon.version += 1;
        coupon.reservation = {
          checkoutRef, candidateSignature, reservedAt: context.at,
          expiresAt: new Date(reservationExpiresAt).toISOString(),
        };
        return { coupon: couponSnapshot(coupon), validatedBenefit: validatedBenefitFor(coupon) };
      });
    },

    releaseReservation(context: BenefitMutationContext, command: ReleaseCouponReservationCommand): IssuedCouponSnapshot {
      return run('releaseReservation', context, context.idempotencyKey, command, () => {
        const coupon = requireCoupon(command.couponId);
        assertVersion(command.expectedVersion);
        if (coupon.version !== command.expectedVersion) throw new Error('BENEFIT_COUPON_VERSION_CONFLICT');
        if (coupon.status !== 'reserved' || !coupon.reservation) throw new Error('BENEFIT_COUPON_NOT_RESERVED');
        if (coupon.reservation.checkoutRef !== assertText(command.checkoutRef, 'BENEFIT_CHECKOUT_REF_REQUIRED')) {
          throw new Error('BENEFIT_CHECKOUT_REF_MISMATCH');
        }
        coupon.status = 'available';
        coupon.version += 1;
        delete coupon.reservation;
        return couponSnapshot(coupon);
      });
    },

    commitRedemption(context: BenefitMutationContext, command: CommitCouponRedemptionCommand): IssuedCouponSnapshot {
      return run('commitRedemption', context, context.idempotencyKey, command, () => {
        const coupon = requireCoupon(command.couponId);
        assertVersion(command.expectedVersion);
        if (coupon.version !== command.expectedVersion) throw new Error('BENEFIT_COUPON_VERSION_CONFLICT');
        if (coupon.status !== 'reserved' || !coupon.reservation) throw new Error('BENEFIT_COUPON_NOT_RESERVED');
        if (coupon.reservation.checkoutRef !== assertText(command.checkoutRef, 'BENEFIT_CHECKOUT_REF_REQUIRED')) {
          throw new Error('BENEFIT_CHECKOUT_REF_MISMATCH');
        }
        coupon.status = 'redeemed';
        coupon.version += 1;
        coupon.redeemedOrderId = assertText(command.orderId, 'BENEFIT_ORDER_ID_REQUIRED');
        coupon.redeemedAt = context.at;
        delete coupon.reservation;
        return couponSnapshot(coupon);
      });
    },

    voidCoupon(context: BenefitMutationContext, command: VoidCouponCommand): IssuedCouponSnapshot {
      return run('voidCoupon', context, context.idempotencyKey, command, () => {
        const coupon = requireCoupon(command.couponId);
        assertVersion(command.expectedVersion);
        if (coupon.version !== command.expectedVersion) throw new Error('BENEFIT_COUPON_VERSION_CONFLICT');
        if (coupon.status === 'redeemed' || coupon.status === 'expired' || coupon.status === 'voided') {
          throw new Error('BENEFIT_COUPON_NOT_VOIDABLE');
        }
        coupon.status = 'voided';
        coupon.version += 1;
        coupon.voidReason = assertText(command.reason, 'BENEFIT_REASON_REQUIRED');
        delete coupon.reservation;
        return couponSnapshot(coupon);
      });
    },

    coupon(couponId: string): IssuedCouponSnapshot | null {
      const coupon = coupons.get(couponId.trim());
      return coupon ? couponSnapshot(coupon) : null;
    },

    customerCoupons(customerId: string): readonly IssuedCouponSnapshot[] {
      const accepted = assertText(customerId, 'BENEFIT_CUSTOMER_ID_REQUIRED');
      return [...coupons.values()].filter((coupon) => coupon.customerId === accepted).map(couponSnapshot);
    },
  };
}
